import React, { useState, useEffect } from 'react';
import { Drawer, Button, Menu } from 'antd';
import { MenuOutlined } from '@ant-design/icons';
import { Link, Outlet, useLocation } from 'react-router-dom';

// Shared Layout component (Header + Footer + Mobile drawer menu)
// Uses <Outlet /> for nested routes so pages do not need to include Layout.
export default function Layout() {
  const [drawerOpen, setDrawerOpen] = useState(false);
  const showDrawer = () => setDrawerOpen(true);
  const closeDrawer = () => setDrawerOpen(false);
  const location = useLocation();

  useEffect(() => {
    if (location.hash) {
      const element = document.querySelector(location.hash);
      if (element) {
        setTimeout(() => {
          const yOffset = -80; // adjust to your header height
          const y =
            element.getBoundingClientRect().top + window.pageYOffset + yOffset;
  
          window.scrollTo({ top: y, behavior: "smooth" });
        }, 100);
      }
    }
  }, [location]);  

  return (
    <div className="min-h-screen bg-gray-50 text-slate-800">
      <header className="sticky top-0 z-40 bg-white/90 backdrop-blur-sm shadow-sm">
        <div className="max-w-6xl mx-auto px-6 lg:px-8">
          <div className="flex items-center justify-between py-4">
            <div className="flex items-center gap-4">
              <Link to="/" className="flex items-center gap-3">
                <div>
                  <div className="text-sm font-semibold">
                    An Advanced Review of Speech – Language Pathology
                  </div>
                  <div className="text-xs text-slate-500">
                    6th Edition: Practice Examinations
                  </div>
                </div>
              </Link>
            </div>

            <nav className="hidden md:flex items-center gap-6 text-sm">
              <Link to="/" className="hover:underline">Home</Link>
              <Link to="/help" className="hover:underline">Help</Link>
              <Link to="/#about" className="hover:underline">About</Link>
              <Link to="/acknowledgements" className="hover:underline">Acknowledgments</Link>
            </nav>

            <div className="flex items-center gap-3">
              <a href="https://www.proedinc.com/products-40018.html" className="hidden md:inline-flex items-center gap-2 px-4 py-2 rounded-full bg-[#a70050] text-white text-sm shadow-sm hover:brightness-95" target="_blank" rel="noopener noreferrer">Buy the book</a>

              <div className="md:hidden">
                <Button type="text" icon={<MenuOutlined />} onClick={showDrawer} aria-label="Open menu" />
                <Drawer placement="right" onClose={closeDrawer} open={drawerOpen}>
                  <Menu mode="vertical" selectable={false}>
                    <Menu.Item key="home">
                      <Link to="/" onClick={closeDrawer}>Home</Link>
                    </Menu.Item>
                    <Menu.Item key="help">
                      <Link to="/help" onClick={closeDrawer}>Help</Link>
                    </Menu.Item>
                    <Menu.Item key="about">
                      <Link to="/#about" onClick={closeDrawer}>About</Link>
                    </Menu.Item>
                    <Menu.Item key="ack">
                      <Link to="/acknowledgements" onClick={closeDrawer}>Acknowledgments</Link>
                    </Menu.Item>
                    <Menu.Item key="buy">
                      <a href="https://www.proedinc.com/products-40018.html" onClick={closeDrawer} target="_blank" rel="noopener noreferrer">Buy</a>
                    </Menu.Item>
                  </Menu>
                </Drawer>
              </div>
            </div>
          </div>
        </div>
      </header>

      <main>
        <Outlet />
      </main>

      <footer id="ack" className="bg-slate-100">
        <div className="max-w-6xl mx-auto px-6 lg:px-8 py-10">
          <div className="grid grid-cols-1 md:grid-cols-3 gap-8">
            <div>
              <h4 className="font-semibold mb-3">Acknowledgments</h4>
              <div className="text-sm">
                <a className="text-[#00AAB5] font-medium" href="https://www.proedinc.com/" target="_blank" rel="noopener noreferrer">PRO-ED, Inc.</a>
                <p className="mt-2 text-sm">For 6th edition:</p>
                <p className="text-sm">Becky Shore, Head of Development and Production</p>
                <p className="text-sm">Beth Rowan: Senior Editor, Development and Production</p>
                <p className="text-sm">Jason Crosier: Art Director</p>
              </div>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Other Links</h4>
              <ul className="space-y-2 text-sm">
                <li><Link to="/#about" className="underline">About the Praxis SLP – ETS</Link></li>
                {/* <li><a className="underline" href="https://www.youtube.com/watch?v=mqHra_E8Q2Y" target="_blank" rel="noopener noreferrer">YouTube Video – Tips to Pass the Praxis SLP</a></li> */}
                {/* <li><a className="underline" href="/question-of-the-day/">Question of the Day</a></li> */}
                <li><a className="underline" href={process.env.REACT_APP_URLS_FOR_BASE + "/references.pdf"} target="_blank" rel="noopener noreferrer">References</a></li>
              </ul>
            </div>

            <div>
              <h4 className="font-semibold mb-3">Terms &amp; Conditions</h4>
              <ul className="space-y-2 text-sm">
                <li><a className="underline" href="https://www.proedsoftware.com/pdfs/Advanced_Review_terms_of_use.pdf" target="_blank" rel="noreferrer">Terms of Use</a></li>
                <li><a className="underline" href="https://www.proedsoftware.com/pdfs/Advanced_Review_privacy_policy.pdf" target="_blank" rel="noreferrer">Privacy Policy</a></li>
              </ul>
            </div>
          </div>

          <div className="mt-8 border-t border-gray-200 pt-6 flex flex-col md:flex-row items-center justify-between text-sm text-slate-600">
            <div>An Advanced Review of Speech – Language Pathology, 6th Edition: Practice Examinations © {new Date().getFullYear()} PRO-ED, Inc.</div>
            <div className="flex items-center gap-4 mt-3 md:mt-0">
              <Link to="/help" className="hover:underline">Help</Link>
              <a href="https://www.proedsoftware.com/pdfs/Advanced_Review_terms_of_use.pdf" className="hover:underline" target="_blank" rel="noopener noreferrer">Terms</a>
              <a href="https://www.proedsoftware.com/pdfs/Advanced_Review_privacy_policy.pdf" className="hover:underline" target="_blank" rel="noopener noreferrer">Privacy</a>
            </div>
          </div>
        </div>
      </footer>
    </div>
  );
}
